// Valantic MDQ — branded PDF report generator (jsPDF + autotable).
// Generates a multi-section executive PDF from an `analysis` payload.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const VAL = {
  red:    [255, 75, 75],
  orange: [255, 116, 79],
  black:  [16, 12, 42],
  muted:  [110, 106, 130],
  silver: [205, 205, 205],
  bg:     [248, 247, 250],
  rose:   [185, 28, 28],
  amber:  [180, 83, 9],
  blue:   [29, 78, 216],
  green:  [4, 120, 87],
};

const sevColorRow = (sev) =>
  sev === "critical" ? VAL.rose
  : sev === "high"   ? VAL.amber
  : sev === "medium" ? VAL.blue
  : VAL.green;

function header(doc, { product, areaName, modules, run }) {
  // Top brand strip
  doc.setFillColor(...VAL.red);
  doc.rect(0, 0, 210, 2, "F");
  doc.setFillColor(...VAL.orange);
  doc.rect(70, 0, 80, 2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...VAL.black);
  doc.text("valantic", 14, 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...VAL.muted);
  doc.text(`Master Data Quality Report · ${product} · ${areaName}`, 14, 19);

  doc.setFontSize(8);
  doc.setTextColor(...VAL.muted);
  const right = `${new Date().toLocaleString("en-GB")}`;
  doc.text(right, 196, 14, { align: "right" });
  if (run?.user_email) {
    doc.text(run.user_email, 196, 19, { align: "right" });
  }

  if (modules?.length) {
    doc.setFontSize(7.5);
    doc.setTextColor(...VAL.black);
    doc.text(`Scope: ${modules.join(" · ")}`, 14, 24);
  }
}

function footer(doc) {
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...VAL.muted);
    doc.text(`valantic · Master Data Quality Analyzer · confidential`, 14, 290);
    doc.text(`${i} / ${total}`, 196, 290, { align: "right" });
  }
}

function summarySection(doc, sum, top, startY) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(...VAL.red);
  doc.text(String(sum.overallScore ?? "—"), 14, startY + 12);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...VAL.muted);
  doc.text("DQ score / 100", 14, startY + 17);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...VAL.black);
  doc.text(`Go-live readiness · ${sum.goLiveReadiness || "—"}`, 40, startY + 9);

  // Badges row
  const badges = [
    [`${sum.criticalGaps || 0} critical`, VAL.rose],
    [`${sum.highGaps || 0} high`, VAL.amber],
    [`${sum.mediumGaps || 0} medium`, VAL.blue],
    [`Remediation · ${sum.estimatedRemediation || "—"}`, VAL.muted],
  ];
  let x = 40;
  badges.forEach(([label, color]) => {
    const w = doc.getTextWidth(label) + 5;
    doc.setFillColor(...color);
    doc.setDrawColor(...color);
    doc.roundedRect(x, startY + 12, w, 5, 1, 1, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text(label, x + 2.5, startY + 15.5);
    x += w + 2;
  });

  // Top blockers
  if (top?.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...VAL.black);
    doc.text("Top blockers", 14, startY + 28);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    top.slice(0, 3).forEach((b, i) => {
      const lines = doc.splitTextToSize(`${String(i + 1).padStart(2, "0")}  ${b}`, 175);
      doc.text(lines, 16, startY + 34 + i * 8);
    });
  }
  return startY + 60;
}

function tableSection(doc, title, head, body, startY) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...VAL.black);
  doc.text(title, 14, startY);
  autoTable(doc, {
    startY: startY + 3,
    head: [head],
    body,
    theme: "grid",
    headStyles: { fillColor: VAL.black, textColor: 255, fontSize: 8, fontStyle: "bold" },
    styles: { fontSize: 7.5, cellPadding: 1.6, overflow: "linebreak", textColor: [40, 40, 40] },
    alternateRowStyles: { fillColor: VAL.bg },
    columnStyles: { 0: { cellWidth: 16 } },
    margin: { left: 14, right: 14 },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 1) {
        const sev = String(data.cell.raw || "").toLowerCase();
        const c = sevColorRow(sev);
        data.cell.styles.textColor = c;
        data.cell.styles.fontStyle = "bold";
      }
    },
  });
  return doc.lastAutoTable.finalY + 6;
}

export function generateMdqPdf({ analysis, product, areaName, modules = [], run = null }) {
  if (!analysis) throw new Error("No analysis to render");
  const sum = analysis.summary || {};
  const ins = analysis.expertInsights || {};
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });

  header(doc, { product, areaName, modules, run });
  let y = summarySection(doc, sum, sum.top3Blockers || [], 32);

  // Expert insights
  const experts = [
    ["Supply Planning Architect", ins.supplyArchitect],
    ["Industry Expert", ins.industryExpert],
    ["Digital Supply Chain Process Expert", ins.processExpert],
    ["Master Data Expert", ins.masterDataExpert],
  ].filter(([, t]) => t);

  if (experts.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...VAL.black);
    doc.text("Expert insights", 14, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [["Expert", "Finding"]],
      body: experts,
      theme: "grid",
      headStyles: { fillColor: VAL.red, textColor: 255, fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
      columnStyles: { 0: { cellWidth: 55, fontStyle: "bold" } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // MD Completeness
  if ((analysis.mdCompleteness || []).length) {
    if (y > 230) { doc.addPage(); y = 25; }
    y = tableSection(doc, "Master Data Completeness", ["Object", "Severity", "Completeness", "Critical missing", "IBP impact"],
      (analysis.mdCompleteness || []).map((c) => [
        c.object,
        c.completeness < 30 ? "critical" : c.completeness < 70 ? "high" : "low",
        `${c.completeness}%`,
        (c.criticalMissing || []).slice(0, 6).join("; "),
        c.ibpImpact,
      ]), y);
  }

  // MD Gaps
  if ((analysis.masterDataGaps || []).length) {
    if (y > 230) { doc.addPage(); y = 25; }
    y = tableSection(doc, "Master Data Gaps", ["ID", "Sev", "Object · Attr", "S/4 source", "Empty %", "Solution"],
      (analysis.masterDataGaps || []).map((g) => [
        g.id, g.sev, `${g.obj || ""} · ${g.attr || ""}`, `${g.s4Tab || ""}.${g.s4Fld || ""}`, `${g.pct || 0}%`, g.solution,
      ]), y);
  }

  // TXN Gaps
  if ((analysis.transactionDataGaps || []).length) {
    if (y > 230) { doc.addPage(); y = 25; }
    y = tableSection(doc, "Transaction Data Gaps", ["ID", "Sev", "Domain", "Description", "Solution"],
      (analysis.transactionDataGaps || []).map((t) => [t.id, t.sev, t.domain, t.desc, t.solution]), y);
  }

  // Plausibility
  if ((analysis.plausibilityChecks || []).length) {
    if (y > 230) { doc.addPage(); y = 25; }
    y = tableSection(doc, "Plausibility Checks", ["ID", "Sev", "Table.Field", "Rule", "Failing", "Fix"],
      (analysis.plausibilityChecks || []).map((p) => [p.id, p.sev, `${p.table}.${p.field}`, p.rule, `${p.pctFailing}%`, p.fix]), y);
  }

  // ERP ↔ IBP Consistency
  if ((analysis.erpConsistencyChecks || []).length) {
    if (y > 230) { doc.addPage(); y = 25; }
    y = tableSection(doc, "ERP ↔ IBP Consistency", ["ID", "Sev", "ERP → IBP", "Finding", "Resolution"],
      (analysis.erpConsistencyChecks || []).map((c) => [c.id, c.sev, `${c.erpField} → ${c.ibpField}`, c.finding, c.resolution]), y);
  }

  // Remediation roadmap
  const rem = analysis.remediation || {};
  const allActions = [
    ...((rem.p0 || []).map((a) => ["P0", a.action, a.owner, a.effort, a.unlocks])),
    ...((rem.p1 || []).map((a) => ["P1", a.action, a.owner, a.effort, a.unlocks])),
    ...((rem.p2 || []).map((a) => ["P2", a.action, a.owner, a.effort, a.unlocks])),
  ];
  if (allActions.length) {
    if (y > 220) { doc.addPage(); y = 25; }
    y = tableSection(doc, "Remediation roadmap", ["Phase", "Action", "Owner", "Effort", "Unlocks"], allActions, y);
  }

  footer(doc);

  const filename = `valantic_MDQ_Report_${product}_${areaName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}
